import React, { createContext, useContext, useEffect, useState } from 'react';
import { SubscriptionContextType, SubscriptionPlan, SubscriptionTier } from './types';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

async function paymentRequest(path: string, body?: Record<string, unknown>) {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'Die Zahlung konnte nicht gestartet werden.');
  return payload;
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const [confirming, setConfirming] = useState(false);
  const tier: SubscriptionTier = user?.subscription?.active || user?.subscriptionTier === 'premium' ? 'premium' : 'free';
  const usesRemaining = tier === 'premium' ? 999 : Math.max(0, user?.freeUsesRemaining ?? 0);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sessionId = query.get('session_id');
    if (query.get('subscription') !== 'success' || !sessionId || confirming) return;
    setConfirming(true);
    paymentRequest('/api/payments/confirm', { sessionId })
      .then(() => refreshUser())
      .finally(() => {
        query.delete('subscription');
        query.delete('session_id');
        const next = `${window.location.pathname}${query.size ? `?${query}` : ''}`;
        window.history.replaceState({}, '', next);
        setConfirming(false);
      });
  }, [confirming, refreshUser]);

  const upgrade = async (plan: SubscriptionPlan) => {
    const payload = await paymentRequest('/api/payments/checkout', { plan });
    if (!payload.url) throw new Error('Stripe hat keine Zahlungsseite zurückgegeben.');
    window.location.assign(payload.url);
  };

  const openBillingPortal = async () => {
    const payload = await paymentRequest('/api/payments/portal');
    if (!payload.url) throw new Error('Das Kundenportal konnte nicht geöffnet werden.');
    window.location.assign(payload.url);
  };

  const decrementUsage = async () => tier === 'premium' || usesRemaining > 0;

  return (
    <SubscriptionContext.Provider value={{
      tier,
      usesRemaining,
      isPremium: tier === 'premium',
      upgrade,
      openBillingPortal,
      decrementUsage,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) throw new Error('useSubscription must be used within SubscriptionProvider');
  return context;
};
